'use client'

import { useEffect, useMemo, useRef } from 'react'

import useEmblaCarousel from 'embla-carousel-react'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu'

import { useCarouselState } from '@/core/components/optional/use-carousel-state'
import { prefersReducedMotion } from '@/core/lib/motion'
import { cn } from '@/core/utils/common'

import type { ReactNode } from 'react'

type DragRailProps = {
  /** 한 칸씩의 항목. 각 자식이 그대로 슬라이드가 되므로 폭(flex-none w-…)은 자식이 정한다 */
  children: ReactNode
  id?: string
  ariaLabel?: string
  className?: string
  /** 항목 줄(gap·padding). 바깥 여백은 className으로 지정한다. */
  classNames?: { track?: string }
  /** PC에서 호버·포커스 중 화살표를 보인다. false이면 숨긴다. */
  showArrows?: boolean
}

/**
 * 가로로 넘기는 목록(카드 레일·칩 줄) — 브라우저 가로 스크롤 대신 Embla 로 움직인다.
 * - 마우스로 끌기(PC)·관성(dragFree)·트랙패드/휠 가로 제스처(wheel gestures)
 * - 화살표는 한 화면 분량씩 넘기고, 더 갈 곳이 없으면 사라진다. 넘칠 게 없으면 끌기도 꺼진다.
 * - 끈 직후의 클릭은 Embla 가 막아 카드 링크가 잘못 열리지 않는다.
 * 항목 사이 간격·좌우 여백은 track 의 gap·padding 으로 준다.
 */
export default function DragRail({
  children,
  id,
  ariaLabel,
  className,
  classNames,
  showArrows = true,
}: DragRailProps) {
  // 세로 휠은 그대로 페이지를 내리고, 가로 제스처(트랙패드·Shift+휠)만 레일을 움직인다
  const plugins = useMemo(() => [WheelGesturesPlugin()], [])
  const [viewportRef, emblaApi] = useEmblaCarousel(
    {
      align: 'start',
      containScroll: 'trimSnaps',
      dragFree: true,
      slidesToScroll: 'auto',
      skipSnaps: true,
    },
    plugins
  )
  const { canScrollPrev, canScrollNext } = useCarouselState(emblaApi, false)
  const dragState = useRef<{ api: typeof emblaApi; enabled: boolean }>({
    api: undefined,
    enabled: true,
  })

  useEffect(() => {
    if (!emblaApi) return
    if (dragState.current.api !== emblaApi)
      dragState.current = { api: emblaApi, enabled: true }
    const syncDrag = () => {
      // 스냅 수는 재초기화/크기 변경 때 바뀐다. 매 스크롤에는 확인하지 않는다.
      const scrollable = emblaApi.scrollSnapList().length > 1
      if (dragState.current.enabled !== scrollable) {
        dragState.current.enabled = scrollable
        emblaApi.reInit({ watchDrag: scrollable })
      }
    }
    syncDrag()
    emblaApi.on('reInit', syncDrag)
    return () => {
      emblaApi.off('reInit', syncDrag)
    }
  }, [emblaApi])

  const arrow = cn(
    'absolute top-1/2 z-[3] size-10 -translate-y-1/2 place-items-center rounded-full border border-foreground/12 bg-[rgb(28_28_30/0.62)] text-white shadow-[0_8px_24px_-8px_rgb(0_0_0/0.8)] backdrop-blur-xl backdrop-saturate-150',
    'cursor-pointer transition-[opacity,background-color] duration-200 hover:bg-[rgb(58_58_60/0.85)] focus:outline-none focus-visible:border-white/50 focus-visible:opacity-100',
    'hidden opacity-0 group-hover/rail:opacity-100 over10:grid'
  )

  return (
    <section
      id={id}
      aria-label={ariaLabel}
      aria-roledescription={ariaLabel ? '가로 목록' : undefined}
      className={cn('group/rail relative', className)}
    >
      <div ref={viewportRef} className="overflow-hidden">
        {/* data-drag-rail-track: 바깥에서 항목 등장 연출(stagger) 등을 걸 때 쓰는 고정 표식 */}
        <div
          data-drag-rail-track
          className={cn(
            'flex touch-pan-y touch-pinch-zoom select-none',
            classNames?.track
          )}
        >
          {children}
        </div>
      </div>
      {showArrows && canScrollPrev && (
        <button
          type="button"
          aria-label="이전"
          onClick={() => emblaApi?.scrollPrev(prefersReducedMotion())}
          className={cn(arrow, 'left-2')}
        >
          <LuChevronLeft aria-hidden="true" className="size-5" />
        </button>
      )}
      {showArrows && canScrollNext && (
        <button
          type="button"
          aria-label="다음"
          onClick={() => emblaApi?.scrollNext(prefersReducedMotion())}
          className={cn(arrow, 'right-2')}
        >
          <LuChevronRight aria-hidden="true" className="size-5" />
        </button>
      )}
    </section>
  )
}
